import { useEffect, useState } from "react";

/**
 * Whether this tab has opened HomeWork, and so belongs on the floor.
 *
 * Presence has to outlive the module. Reading your email is still being at
 * work, so navigating to another module must not clock you out — but somebody
 * who has never opened HomeWork should not be stood on the floor either, and
 * closing the tab has to take you off it.
 *
 * `sessionStorage` answers exactly that question: it is per-tab, it survives a
 * reload (where the socket simply reconnects), and the browser clears it when
 * the tab closes. Nothing here disconnects anybody; it only decides whether
 * the socket should be open at all. Leaving is the socket closing, which the
 * server already handles.
 */
const KEY = "weco.homework.entered";

const listeners = new Set<() => void>();

export function hasEnteredOffice(): boolean {
  try {
    return window.sessionStorage.getItem(KEY) === "1";
  } catch {
    // Private windows and blocked site data throw rather than return null.
    // Falling back to "not entered" keeps a user off the floor rather than
    // silently on it, which is the safer way to be wrong about presence.
    return false;
  }
}

export function markOfficeEntered(): void {
  if (hasEnteredOffice()) return;
  try {
    window.sessionStorage.setItem(KEY, "1");
  } catch {
    // Unwritable storage is handled below: the in-memory notify still runs,
    // so this tab connects — it just will not survive a reload.
  }
  for (const listener of listeners) listener();
}

/**
 * Take this tab off the floor — the person checked out for the day.
 *
 * The socket closing is what the server already treats as leaving; this only
 * stops the shell reopening it until they check in again.
 */
export function markOfficeLeft(): void {
  if (!hasEnteredOffice()) return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear if storage is unavailable; the notify below still runs.
  }
  for (const listener of listeners) listener();
}

export function subscribeOfficeEntered(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-renders when this tab enters HomeWork, or leaves it by checking out. */
export function useOfficeEntered(): boolean {
  const [entered, setEntered] = useState(hasEnteredOffice);
  useEffect(() => {
    // Storage may have been written before the listener attached (the module
    // marks entry on mount, which can run first).
    setEntered(hasEnteredOffice());
    return subscribeOfficeEntered(() => setEntered(hasEnteredOffice()));
  }, []);
  return entered;
}
